import type { Poem } from '@maria-cavalheiro-website/shared-types'

import { findPoemBySlug, listPoems } from '../features/poems/poem-collection.ts'

export type Route =
  | { readonly kind: 'home' }
  | { readonly kind: 'about' }
  | { readonly kind: 'poem'; readonly poem: Poem }
  | { readonly kind: 'not-found' }

export const HOME_PATH = '/'

/**
 * The biography, which used to be `#sobre` on the home document.
 *
 * A route rather than a section because it is about a different subject: the home
 * document is the collection, and a page whose `h1` is the poet's name is a
 * different thing for a crawler, a canonical URL and a shared link alike. The nav
 * label did not change, so nothing a reader knows about the site did.
 */
export const ABOUT_PATH = '/sobre'

/** Sentinel path prerendered into `dist/404.html`; never linked, never indexed. */
export const NOT_FOUND_PATH = '/__not-found__'

const POEM_PATH_PATTERN = /^\/poemas\/([a-z0-9]+(?:-[a-z0-9]+)*)$/

export function poemPath(slug: string): string {
  return `/poemas/${slug}`
}

/** Drops a single trailing slash so `/poemas/x/` and `/poemas/x` resolve identically. */
export function normalizePathname(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

/**
 * Pure, total path -> route resolution. O(1): one regex match plus one Map probe.
 * Resolution carries the poem itself, so downstream renderers never re-lookup and
 * never have to handle an impossible "route exists but poem missing" state.
 */
export function resolveRoute(pathname: string): Route {
  const normalized = normalizePathname(pathname)

  if (normalized === HOME_PATH || normalized === '') {
    return { kind: 'home' }
  }

  if (normalized === ABOUT_PATH) {
    return { kind: 'about' }
  }

  const slug = POEM_PATH_PATTERN.exec(normalized)?.[1]
  if (slug !== undefined) {
    const poem = findPoemBySlug(slug)
    if (poem !== undefined) {
      return { kind: 'poem', poem }
    }
  }

  return { kind: 'not-found' }
}

/** Every path the static site generator materialises into `dist/`. */
export function listStaticRoutePaths(): readonly string[] {
  return [HOME_PATH, ABOUT_PATH, ...listPoems().map((poem) => poemPath(poem.slug))]
}
